
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  MypageRecipeClips,
  FolderInfo,
  MypageFolders,
  MypageInquiryStatus,
  MypageFoodsRanking,
  MypageUserRanking,
  MypageHistoryRecipes
} from "@/types/mypage";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import FavoritesFolderNameInputDialog from "@/components/favorites/FavoritesFolderNameInputDialog.vue";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import PullToRefresh from "@/components/common/PullToRefresh.vue";
import MypageReports from "@/components/mypage/MypageReports.vue"
import MypageFavorites from "@/components/mypage/MypageFavorites.vue";
import MypageSelectTabs from "@/components/mypage/MypageSelectTabs.vue";
import { RecipeFoods } from "@/types/pantry";
import router from "@/router";

export default defineComponent({
  name: "MypagePage",
  components: { 
    MypageReports,
    MypageFavorites,
    FavoritesFolderNameInputDialog,
    CommonDesignDialog,
    MypageSelectTabs,
    PullToRefresh
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(true, ""));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // フォルダ名入力のためのリアクティブ
    const updateInputFolderNameKey = ref(0);
    const updateInputFolderName = () => {
      if (updateInputFolderNameKey.value == 100) {
        updateInputFolderNameKey.value = 0;
      } else {
        updateInputFolderNameKey.value++;
      }
    };

    // ダイアログ情報 API実行エラーのダイアログは、割り込まれるので、API実行前に、以下の情報で判別する
    class MypageDialogInfo {
      type: number;         // 0:なし、1:取得関連API、2：フォルダ追加API実行時、3:フォルダ移動API実行, 4:その他
      negativeCallback: (() => void) | null;
      positiveCallback: (() => void) | null;
      constructor(type: number, negativeCallback: (() => void) | null, positiveCallback: (() => void) | null) {
        this.type = type;
        this.negativeCallback = negativeCallback;
        this.positiveCallback = positiveCallback;
      }
    } 
    const refDialogInfo = ref(new MypageDialogInfo(0, null, null));

    // 汎用のダイアログ
    const isShowDialog = ref(false);
    const dialogType = ref(0);
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const dialogPositiveButtonLabel = ref("");
    const dialogNegativeButtonLabel = ref("");
    const dialogPositiveHandler = () => {
      isShowDialog.value = false;
      if (refDialogInfo.value.positiveCallback != null) {
        refDialogInfo.value.positiveCallback();
      }
    };
    const dialogNegativeHandler = () => {
      isShowDialog.value = false;
      if (refDialogInfo.value.negativeCallback != null) {
        refDialogInfo.value.negativeCallback();
      }
    };
    const dialogCloseHandler = () => {
      isShowDialog.value = false;
    };

    // フォルダー上限通知ダイアログ
    const displayFolderLimit = () => {
      dialogType.value = 9;
      dialogTitle.value = "フォルダ追加";
      dialogMessage.value = "フォルダは10個までしか登録することができません。";
      dialogPositiveButtonLabel.value = "";
      dialogNegativeButtonLabel.value = "OK";
      refDialogInfo.value = new MypageDialogInfo(4, null, null);
      isShowDialog.value = true;
    };

    // フォルダ追加ダイアログ
    const isShowAppendFolderDialog = ref(false);
    const closeAppendFolderDialog = () => {
      isShowAppendFolderDialog.value = false;
    };
    let appendCallback: ((name: string) => void) | null;
    const appendFolderAction = (name: string) => {
      isShowAppendFolderDialog.value = false;
      if (appendCallback != null) {
        appendCallback(name);
      }
    };
    const displayAppendFolderDialog = (callback: ((name: string) => void)) => {
      appendCallback = callback;
      isShowAppendFolderDialog.value = true;
    };

    //
    // API実行
    //

    // お気に入り食品取得
    const getFavoriteFoodList = async (valid: boolean) => {
      NativeClient.sendDebugLog("MypagePage getFavoriteFoodList() valid = " + valid);
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getFavoriteFoodList")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getFavoriteFoodList() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getFavoriteFoodList() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // お気に入りレシピ取得
    const getFavoriteRecipeList = async (valid: boolean) => {
      NativeClient.sendDebugLog("MypagePage getFavoriteRecipeList() valid = " + valid);
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getRecentFavoriteRecipeList")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getFavoriteRecipeList() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getFavoriteRecipeList() no update");
          resolve(true);
        }
      });
      return promise;
    };

    // フォルダ一覧
    const getFolderList = async (valid: boolean) => {
      NativeClient.sendDebugLog("MypagePage getFolderList() valid = " + valid);
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getFolderList")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getFolderList() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getFolderList() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 問診状態取得
    const getInquirtStatus = async (valid: boolean) => {
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getInquiryStatus")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getInquirtStatus() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getInquirtStatus() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // 使い切り食材情報取得
    const getUsedFoodsRanking = async (valid: boolean) => {
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getUsedFoodsRanking")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getUsedFoodsRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getUsedFoodsRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // ユーザー・ランキング
    const getUserRanking = async (valid: boolean) => {
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getUserRanking")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getUserRanking() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getUserRanking() no update");
          resolve(true);
        }
      });
      return promise;
    };
    // レシピ履歴
    const getHistoryRecipe = async (valid: boolean) => {
      const promise = new Promise((resolve) => {
        if (valid) {
          Promise.all([
            store.dispatch("mypage/getTopHistoryRecipe")
          ]).then((result) => {
            NativeClient.sendDebugLog("MypagePage getHistoryRecipe() complete");
            resolve(true);
          });
        } else {
          NativeClient.sendDebugLog("MypagePage getHistoryRecipe() no update");
          resolve(true);
        }
      });
      return promise;
    };

    // フォルダ登録
    const registerFolder = async (name: string, successCallback: (() => void)) => {
      NativeClient.sendDebugLog("MypagePage getFolderList()");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("mypage/registFolder", name);
      store.dispatch("common/stopLoading");
      successCallback();
    };

    // フォルダ移動
    const moveFolder = async (folderId: number, position: number, successCallback: (() => void)) => {
      NativeClient.sendDebugLog("MypagePage moveFolder()");
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await store.dispatch("mypage/moveFolder", { folderId: folderId, position: position });
      store.dispatch("common/stopLoading");
      successCallback();
    };

    // 一覧更新
    class MypagePageUpdateInfo {
      favoriteFood: boolean;
      favoriteRecipeList: boolean;
      folderList: boolean;
      inquiryStatus: boolean;
      usedFoodsRanking: boolean;
      userRanking: boolean;
      historyRecipe: boolean;

      constructor() {
        this.favoriteFood = false;
        this.favoriteRecipeList = false;
        this.folderList = false;
        this.inquiryStatus = false;
        this.usedFoodsRanking = false;
        this.userRanking = false;
        this.historyRecipe = false;
      }
    }
    let updateAPIInfo = new MypagePageUpdateInfo();
    const updateAPIExecute = async () => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      await Promise.all([
        getFavoriteFoodList(updateAPIInfo.favoriteFood),
        getFavoriteRecipeList(updateAPIInfo.favoriteRecipeList),
        getFolderList(updateAPIInfo.folderList),
        getInquirtStatus(updateAPIInfo.inquiryStatus),
        getUsedFoodsRanking(updateAPIInfo.usedFoodsRanking),
        getUserRanking(updateAPIInfo.userRanking),
        getHistoryRecipe(updateAPIInfo.historyRecipe)
      ]);
      store.dispatch("common/stopLoading");
      updateDisplay();
    };
    const updatePageListRetryHandler = () => {
      updateAPIExecute();
    };
    const updatePageList = async (info: MypagePageUpdateInfo) => {
      NativeClient.sendDebugLog("MypagePage updatePageList()");
      updateAPIInfo = info;
      refDialogInfo.value = new MypageDialogInfo(1, null, updatePageListRetryHandler);
      updateAPIExecute();
    };

    // 横スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const changeTab = (index: number) => {
      NativeClient.sendDebugLog("MypagePage changeTab() index = " + index);
      if (scroll.value != undefined) {
        NativeClient.sendDebugLog("HorizontalPaging changeTab() left = " + (scroll.value!.scrollWidth / 2) * index);
        scroll.value!.scrollTo({ top: 0, left: (scroll.value!.scrollWidth / 2 * index) });
      }

      // ビーコンログ送信
      let pageID = PAGE_ID.MYPAGE_TOP_RECIPE;
      if (index == 0) {
        pageID = PAGE_ID.MYPAGE_TOP_REPORT;
      }
      const logInfo = new BeaconLogInfo(pageID, null, []);
      NativeClient.sendViewedLog(logInfo);
    };
  
    // リポート/お気に入り選択状態の変数
    const selectedTab = {
      get value() {
        const index = store.getters["mypage/getSelectedTab"];
        return index;
      },
      set value(tabIndex: number) {
        changeTab(tabIndex);
        store.dispatch("mypage/updateSelectedTab", tabIndex);
      }
    };

    const initislSelectTab = computed(() => {
      const tabIndex: number = store.getters["mypage/getSelectedTab"];
      return tabIndex;
    });

    // テキスト定義
    const reportTitle = "レポート";
    const favoritesTitle = "お気に入り";
  
    // テキスト装飾
    const styleOpacitySelectedReport = computed(() => {
      NativeClient.sendDebugLog("MypagePage styleTextColorSelectedReport()");
      if (selectedTab.value == 0) {
        NativeClient.sendDebugLog("MypagePage select to Histories in styleOpacitySelectedReport");
        return { opacity: 1.0 };
      }
      return { opacity: 0.0 };
    });
    const styleTextColorSelectedReport = computed(() => {
      NativeClient.sendDebugLog("FavoritesPage select Histories button in styleTextColorSelectedReport");
      if (selectedTab.value == 0) {
        NativeClient.sendDebugLog("FavoritesPage select to Report");
        return { color: `#000000` };
      }
      return  { color: `#707070`};
    });
    const styleOpacitySelectedFavorites = computed(() => {
      NativeClient.sendDebugLog("MypagePage select Favorites button in styleOpacitySelectedMylist");
      if (selectedTab.value == 1) {
        NativeClient.sendDebugLog("MypagePage select to Mylist in styleOpacitySelectedMylist");
        return { opacity: 1.0 };
      }
      return { opacity: 0.0 };
    });
    const styleTextColorSelectedFavorites = computed(() => {
      NativeClient.sendDebugLog("MypagePage select Favorites button in styleTextColorSelectedMylist");
      if (selectedTab.value == 1) {
        NativeClient.sendDebugLog("MypagePage select to Mylist");
        return { color: `#000000` };
      }
      return { color: `#707070` };
    });

    // 選択ボタンのハンドラ
    const selectTabs = (index: number) => {
      NativeClient.sendDebugLog("MypagePage selectTabs index = " + index);
      selectedTab.value = index;
      switch (index) {
        case 0:
          setLogId(PAGE_ID.MYPAGE_TOP_REPORT);
          break;
        case 1:
          setLogId(PAGE_ID.MYPAGE_TOP_RECIPE);
          break;
        default:
          break;
      }
    }

    //
    // レポート関連データ
    //
    const inquiryStatus = computed(() => {
      const inquiry: MypageInquiryStatus | null = store.getters["mypage/getInquiryStatus"];
      NativeClient.sendDebugLog("MypagePage inquiryStatus = " + inquiry);
      if (inquiry == null) {
        return undefined;
      }
      return inquiry;
    });
    const usedFoodList = computed(() => {
      const usedFoodList: MypageFoodsRanking | null = store.getters["mypage/getFoodsRanking"];
      NativeClient.sendDebugLog("MypagePage usedFoodList = " + usedFoodList);
      if (usedFoodList == null) {
        return undefined;
      }
      return usedFoodList;
    });
    const userRanking = computed(() => {
      const userRanking: MypageUserRanking | null = store.getters["mypage/getUserRanking"];
      NativeClient.sendDebugLog("MypagePage userRanking = " + userRanking);
      if (userRanking == null) {
        return undefined;
      }
      return userRanking;
    });
    const historyRecipeList = computed(() => {
      const historyRecipeList: MypageHistoryRecipes | null = store.getters["mypage/getTopHistoryRecipe"];
      NativeClient.sendDebugLog("MypagePage historyRecipeList = " + JSON.stringify(historyRecipeList));
      if (historyRecipeList == null) {
        return undefined;
      }
      return historyRecipeList;
    });

    // レポートのボタン処理
    const clickInquiryStart = () => {
      NativeClient.sendDebugLog("MypagePage clickInquiryStart");
    };
    const clickInquiryDetail = () => {
      NativeClient.sendDebugLog("MypagePage clickInquiryDetail");
    };
    const clickUsed = () => {
      NativeClient.sendDebugLog("MypagePage clickUsed");

    };
    //
    // お気に入りのデータ
    //
    const foodList = computed(() => {
      NativeClient.sendDebugLog("MypagePage foodList");
      const result: RecipeFoods | null = store.getters["mypage/getFavoriteFoodList"];
      if (result == null || result == null) {
        NativeClient.sendDebugLog("MypagePage foodList null");
        return null;
      }
      return result.foods;
    });
    const recipeList = computed(() => {
      NativeClient.sendDebugLog("MypagePage recipeList");
      const result: MypageRecipeClips | null = store.getters["mypage/getRecentFavoriteRecipeList"];
      if (result == null || result.clips == null) {
        NativeClient.sendDebugLog("MypagePage recipeList null");
        return null;
      }
      return result.clips;
    });
    let numFolders = 0;
    const folderList = computed(() => {
      NativeClient.sendDebugLog("MypagePage folderList");
      const result: MypageFolders | null = store.getters["mypage/getFolderList"];
      if (result == null || result.folders == null) {
        NativeClient.sendDebugLog("MypagePage folderList null");
        return null;
      }
      const folderList: FolderInfo[] = [];
      for (const folderElement of result.folders) {
        const element = new FolderInfo();
        element.id = folderElement.favoriteFolderId;
        element.name = folderElement.name;
        element.image = folderElement.imageUrl;
        element.numberOfItems = folderElement.clipCount;
        folderList.push(element);
      }
      numFolders = folderList.length;
      return folderList;
    });
    const appendSuccessHandler = () => {
      const info = new MypagePageUpdateInfo();
      info.folderList = true;
      updatePageList(info);
    };
    const appendFolderCallback = (name: string) => {
      registerFolder(name, appendSuccessHandler);
    };
    const appendFolderRequest = () => {
      NativeClient.sendDebugLog("MypagePage appendFolderRequest");
      if (numFolders < COMMON_LIMIT_NUM.FAVORITES_FOLDER_LIMIT) {
        displayAppendFolderDialog(appendFolderCallback);
        updateInputFolderName();
      } else {
        // 上限値オーバー
        displayFolderLimit();
      }
    };
    const moveSuccessHandler = () => {
      const info = new MypagePageUpdateInfo();
      info.folderList = true;
      updatePageList(info);
    };
    const moveFolderRequest = async (folderId: number, position: number) => {
      store.dispatch("mypage/updateMoveFolder", null);
      NativeClient.sendDebugLog("MypagePage replace folder folderId = " + folderId + ", position = " + position);
      moveFolder(folderId, position, moveSuccessHandler);
    };

    // リフレッシュ
    const refreshReport = () => {
      NativeClient.sendDebugLog("MypagePage refreshReport");
      const updateInfo = new MypagePageUpdateInfo();
      updateInfo.favoriteFood = false;
      updateInfo.favoriteRecipeList = false;
      updateInfo.folderList = false;
      updateInfo.inquiryStatus = true;
      updateInfo.usedFoodsRanking = true;
      updateInfo.userRanking = true;
      updateInfo.historyRecipe = true;
      updatePageList(updateInfo);
    };
    const refreshFavorites = () => {
      NativeClient.sendDebugLog("MypagePage refreshFavorites");
      const updateInfo = new MypagePageUpdateInfo();
      updateInfo.favoriteFood = true;
      updateInfo.favoriteRecipeList = true;
      updateInfo.folderList = true;
      updateInfo.inquiryStatus = true;
      updateInfo.usedFoodsRanking = false;
      updateInfo.userRanking = false;
      updateInfo.historyRecipe = false;
      updatePageList(updateInfo);
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage retryApiErrorHandler");
      closeDialog();
      // if (refDialogInfo.value.positiveCallback != null) {
      //   NativeClient.sendDebugLog("MypagePage retryApiErrorHandler positiveCallback exist");
      //   refDialogInfo.value.positiveCallback();
      // }
    };
    const okApiErrorHandler = () => {
      NativeClient.sendDebugLog("MypagePage okApiErrorHandler");
      closeDialog();
    };
    
    // 一覧更新（Nativeのインターフェースから）
    const updatePage = async (info: UpdatePageInfo) => {
      NativeClient.sendDebugLog("FavoritesPage updatePage()");
      const updateInfo = new MypagePageUpdateInfo();
      updateInfo.favoriteFood = info.favoriteFoods;
      updateInfo.favoriteRecipeList = info.myList;
      updateInfo.folderList = info.myList;
      updateInfo.inquiryStatus = false;
      updateInfo.usedFoodsRanking = false;
      updateInfo.userRanking = false;
      updateInfo.historyRecipe = info.viewedHistory;
      updatePageList(updateInfo);
    };

    // 初期
    onMounted(() => {
      const info = new MypagePageUpdateInfo();
      info.favoriteFood = true;
      info.favoriteRecipeList = true;
      info.folderList = true;
      info.inquiryStatus = true;
      info.usedFoodsRanking = true;
      info.userRanking = true;
      info.historyRecipe = true;
      updatePageList(info);

      // マイリスト更新 
      NativeClient.registUpdatePageListener(updatePage);

      changeTab(selectedTab.value);
    });

    return {
      updateDisplayKey,
      updateInputFolderNameKey,
      initislSelectTab,
      scroll,
      reportTitle,
      favoritesTitle,
      styleOpacitySelectedReport,
      styleTextColorSelectedReport,
      styleOpacitySelectedFavorites,
      styleTextColorSelectedFavorites,
      selectTabs,
      refreshReport,
      refreshFavorites,
      foodList,
      recipeList,
      folderList,
      inquiryStatus,
      usedFoodList,
      userRanking,
      historyRecipeList,
      appendFolderRequest,
      moveFolderRequest,
      isShowAppendFolderDialog,
      closeAppendFolderDialog,
      appendFolderAction,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler,
      isShowDialog,
      dialogType,
      dialogTitle,
      dialogMessage,
      dialogPositiveButtonLabel,
      dialogNegativeButtonLabel,
      dialogPositiveHandler,
      dialogNegativeHandler,
      dialogCloseHandler
    };
  }
});
